// Phone number presentation.
//
// Numbers are STORED as bare digits and formatted only at the edges — on the way
// onto the screen and on the way into an input. Nothing downstream parses these
// strings for meaning: the API normalises with NormalizeToE164 (a bare 10-digit
// becomes +1XXXXXXXXXX) and matches inbound texts on Last10Digits, both of which
// strip separators anyway. Formatting is therefore free to be purely cosmetic.

// Just the digits, which is what gets saved.
export const phoneDigits = (raw) => (raw == null ? '' : String(raw).replace(/\D/g, ''));

// A US number down to its 10 significant digits, or null when it isn't one.
// A leading country-code 1 is dropped; anything else (an extension, an
// international number, a half-typed value) returns null so callers can leave it
// alone rather than render something wrong with confidence.
const tenDigits = (raw) => {
    let d = phoneDigits(raw);
    if (d.length === 11 && d[0] === '1') d = d.slice(1);
    return d.length === 10 ? d : null;
};

// For display: "(615) 778-1440".
//
// Anything that isn't a plain US 10-digit number is returned untouched. The
// imported JobNimbus contacts include extensions and the occasional note typed
// into a phone field, and mangling those into a fake number would lose the only
// copy of how to reach someone.
export const formatPhone = (raw) => {
    if (raw == null || raw === '') return '';
    const d = tenDigits(raw);
    return d ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(raw);
};

// What to persist. A clean US number is stored as bare digits so the column holds
// one shape; anything else is saved exactly as typed. The imported contacts
// include extensions and the odd note in a phone field, and stripping those to
// digits would silently turn "615-555-1234 x22" into a number that rings the
// wrong desk.
export const phoneForSave = (raw) => {
    const d = tenDigits(raw);
    return d ?? (raw == null ? '' : String(raw));
};

// For an input, as the user types. Partial values format as far as they go, and
// no separator is ever appended before the digit that follows it — a trailing
// "-" would be re-added the instant it was backspaced over, which reads as the
// delete key being broken.
export const formatPhoneInput = (raw) => {
    let d = phoneDigits(raw);
    if (d.length === 11 && d[0] === '1') d = d.slice(1);
    d = d.slice(0, 10);

    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
